import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Save,
  CheckCircle,
  User,
  Award,
  Star,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const PerformanceReviewFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAdminAuth();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Basic Info
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [reviewYear, setReviewYear] = useState('2024');
  const [reviewType, setReviewType] = useState('annual');
  
  // Employee Data (loaded after selection)
  const [employeeData, setEmployeeData] = useState(null);

  // Professional Competence (الكفاءة المهنية)
  const [professionalScores, setProfessionalScores] = useState({
    accuracy: { score: 0, notes: '' },
    quality: { score: 0, notes: '' },
    deadlines: { score: 0, notes: '' },
    technical: { score: 0, notes: '' }
  });

  // Work Behavior (السلوك الوظيفي)
  const [behaviorScores, setBehaviorScores] = useState({
    discipline: { score: 0, notes: '' },
    policies: { score: 0, notes: '' },
    teamwork: { score: 0, notes: '' },
    punctuality: { score: 0, notes: '' }
  });

  // Personal Skills (المهارات الشخصية)
  const [personalScores, setPersonalScores] = useState({
    communication: { score: 0, notes: '' },
    initiative: { score: 0, notes: '' },
    responsibility: { score: 0, notes: '' },
    problemSolving: { score: 0, notes: '' }
  });

  // Manager Notes & Development Plan
  const [managerNotes, setManagerNotes] = useState('');
  const [developmentPlan, setDevelopmentPlan] = useState({
    strengths: '',
    improvements: '',
    training: ''
  });

  // Mock employees list
  const employees = [
    { id: 'EMP-001', name: 'أحمد محمد العتيبي', position: 'محامي أول', department: 'القسم القانوني', manager: 'سعود المطيري' },
    { id: 'EMP-002', name: 'فاطمة سعيد الغامدي', position: 'مساعد إداري', department: 'القسم الإداري', manager: 'سعود المطيري' },
    { id: 'EMP-003', name: 'خالد عبدالله الشمري', position: 'محاسب', department: 'القسم المالي', manager: 'سعود المطيري' }
  ];

  const reviewTypes = [
    { id: 'annual', label: 'تقييم سنوي' },
    { id: 'semi-annual', label: 'تقييم نصف سنوي' },
    { id: 'quarterly', label: 'تقييم ربع سنوي' },
    { id: 'trial', label: 'تقييم تجريبي' }
  ];

  // Calculate average score
  const calculateAverage = () => {
    const allScores = [
      ...Object.values(professionalScores).map(s => s.score),
      ...Object.values(behaviorScores).map(s => s.score),
      ...Object.values(personalScores).map(s => s.score)
    ];
    const validScores = allScores.filter(s => s > 0);
    if (validScores.length === 0) return 0;
    return (validScores.reduce((a, b) => a + b, 0) / validScores.length).toFixed(2);
  };

  // Convert score to rating
  const getRatingFromScore = (score) => {
    if (score >= 4.5) return { label: 'ممتاز', color: 'green' };
    if (score >= 3.5) return { label: 'جيد جداً', color: 'blue' };
    if (score >= 2.5) return { label: 'جيد', color: 'yellow' };
    if (score >= 1.5) return { label: 'يحتاج تطوير', color: 'orange' };
    return { label: 'ضعيف', color: 'red' };
  };

  const averageScore = parseFloat(calculateAverage());
  const finalRating = getRatingFromScore(averageScore);

  // Handle employee selection
  const handleEmployeeChange = (empId) => {
    setSelectedEmployee(empId);
    const emp = employees.find(e => e.id === empId);
    setEmployeeData(emp);
  };

  // Update score
  const updateScore = (category, field, type, value) => {
    if (category === 'professional') {
      setProfessionalScores(prev => ({
        ...prev,
        [field]: { ...prev[field], [type]: value }
      }));
    } else if (category === 'behavior') {
      setBehaviorScores(prev => ({
        ...prev,
        [field]: { ...prev[field], [type]: value }
      }));
    } else if (category === 'personal') {
      setPersonalScores(prev => ({
        ...prev,
        [field]: { ...prev[field], [type]: value }
      }));
    }
  };

  // Handle save
  const handleSave = (action) => {
    if (!selectedEmployee) {
      alert('يرجى اختيار موظف');
      return;
    }

    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      let message = '';
      if (action === 'draft') message = 'تم حفظ التقييم كمسودة';
      else if (action === 'manager') message = 'تم اعتماد التقييم من المدير المباشر';
      else if (action === 'hr') message = 'تم الاعتماد النهائي من HR';
      alert(message);
      navigate('/admin/hr/performance/reviews');
    }, 1500);
  };

  const renderStarRating = (category, field, currentScore) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => updateScore(category, field, 'score', star)}
            className="transition-all"
          >
            <Star
              className={`w-6 h-6 ${
                star <= currentScore
                  ? 'text-[#C8A870] fill-[#C8A870]'
                  : 'text-gray-300'
              } hover:scale-110`}
            />
          </button>
        ))}
        <span className="mr-2 font-bold text-gray-700">{currentScore > 0 ? currentScore : '-'}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin/hr/performance/reviews')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
          <span className="font-semibold">العودة لقائمة التقييمات</span>
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
            <Award className="w-8 h-8" />
            <span>{isEditMode ? 'تعديل تقييم' : 'تقييم أداء جديد'}</span>
          </h1>
          <p className="text-gray-600">إنشاء تقييم شامل لأداء الموظف</p>
        </div>

        <form className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#1E3A8A]" />
              بيانات التقييم الأساسية
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Employee Selection */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الموظف <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
                >
                  <option value="">اختر الموظف</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} ({emp.id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">السنة</label>
                <input
                  type="text"
                  value={reviewYear}
                  onChange={(e) => setReviewYear(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="2024"
                />
              </div>
            </div>

            {/* Review Type */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">نوع التقييم</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {reviewTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setReviewType(type.id)}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      reviewType === type.id
                        ? 'bg-[#1E3A8A] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Employee Data Display */}
            {employeeData && (
              <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">المسمى الوظيفي</p>
                    <p className="font-bold text-gray-800">{employeeData.position}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">القسم</p>
                    <p className="font-bold text-gray-800">{employeeData.department}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">المدير المباشر</p>
                    <p className="font-bold text-gray-800">{employeeData.manager}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 1: Professional Competence */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#1E3A8A]" />
              الكفاءة المهنية
            </h2>
            
            {/* Accuracy */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">الدقة في العمل</label>
                {renderStarRating('professional', 'accuracy', professionalScores.accuracy.score)}
              </div>
              <textarea
                value={professionalScores.accuracy.notes}
                onChange={(e) => updateScore('professional', 'accuracy', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Quality */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">جودة المخرجات</label>
                {renderStarRating('professional', 'quality', professionalScores.quality.score)}
              </div>
              <textarea
                value={professionalScores.quality.notes}
                onChange={(e) => updateScore('professional', 'quality', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Deadlines */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">الالتزام بالمواعيد النهائية</label>
                {renderStarRating('professional', 'deadlines', professionalScores.deadlines.score)}
              </div>
              <textarea
                value={professionalScores.deadlines.notes}
                onChange={(e) => updateScore('professional', 'deadlines', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Technical Knowledge */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">المعرفة التقنية والمهنية</label>
                {renderStarRating('professional', 'technical', professionalScores.technical.score)}
              </div>
              <textarea
                value={professionalScores.technical.notes}
                onChange={(e) => updateScore('professional', 'technical', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>
          </div>

          {/* Section 2: Work Behavior */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#1E3A8A]" />
              السلوك الوظيفي
            </h2>
            
            {/* Discipline */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">الانضباط والحضور</label>
                {renderStarRating('behavior', 'discipline', behaviorScores.discipline.score)}
              </div>
              <textarea
                value={behaviorScores.discipline.notes}
                onChange={(e) => updateScore('behavior', 'discipline', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Policies */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">الالتزام بالسياسات واللوائح</label>
                {renderStarRating('behavior', 'policies', behaviorScores.policies.score)}
              </div>
              <textarea
                value={behaviorScores.policies.notes}
                onChange={(e) => updateScore('behavior', 'policies', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Teamwork */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">التعاون مع الفريق</label>
                {renderStarRating('behavior', 'teamwork', behaviorScores.teamwork.score)}
              </div>
              <textarea
                value={behaviorScores.teamwork.notes}
                onChange={(e) => updateScore('behavior', 'teamwork', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Punctuality */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">الالتزام بأوقات العمل</label>
                {renderStarRating('behavior', 'punctuality', behaviorScores.punctuality.score)}
              </div>
              <textarea
                value={behaviorScores.punctuality.notes}
                onChange={(e) => updateScore('behavior', 'punctuality', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>
          </div>

          {/* Section 3: Personal Skills */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#1E3A8A]" />
              المهارات الشخصية
            </h2>
            
            {/* Communication */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">مهارات التواصل</label>
                {renderStarRating('personal', 'communication', personalScores.communication.score)}
              </div>
              <textarea
                value={personalScores.communication.notes}
                onChange={(e) => updateScore('personal', 'communication', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Initiative */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">المبادرة والإبداع</label>
                {renderStarRating('personal', 'initiative', personalScores.initiative.score)}
              </div>
              <textarea
                value={personalScores.initiative.notes}
                onChange={(e) => updateScore('personal', 'initiative', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Responsibility */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">تحمل المسؤولية</label>
                {renderStarRating('personal', 'responsibility', personalScores.responsibility.score)}
              </div>
              <textarea
                value={personalScores.responsibility.notes}
                onChange={(e) => updateScore('personal', 'responsibility', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>

            {/* Problem Solving */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">حل المشكلات واتخاذ القرار</label>
                {renderStarRating('personal', 'problemSolving', personalScores.problemSolving.score)}
              </div>
              <textarea
                value={personalScores.problemSolving.notes}
                onChange={(e) => updateScore('personal', 'problemSolving', 'notes', e.target.value)}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                placeholder="ملاحظات (اختياري)"
              />
            </div>
          </div>

          {/* Manager Notes */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4">ملاحظات المدير المباشر</h2>
            <textarea
              value={managerNotes}
              onChange={(e) => setManagerNotes(e.target.value)}
              rows="4"
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              placeholder="ملاحظات عامة حول أداء الموظف خلال الفترة..."
            />
          </div>

          {/* Development Plan */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4">خطة التطوير</h2>
            
            <div className="space-y-4">
              {/* Strengths */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نقاط القوة</label>
                <textarea
                  value={developmentPlan.strengths}
                  onChange={(e) => setDevelopmentPlan({ ...developmentPlan, strengths: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="المهارات والصفات الإيجابية التي يتميز بها الموظف..."
                />
              </div>

              {/* Improvements */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نقاط التحسين</label>
                <textarea
                  value={developmentPlan.improvements}
                  onChange={(e) => setDevelopmentPlan({ ...developmentPlan, improvements: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="المجالات التي تحتاج إلى تطوير وتحسين..."
                />
              </div>

              {/* Training Recommendations */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">التوصيات التدريبية</label>
                <textarea
                  value={developmentPlan.training}
                  onChange={(e) => setDevelopmentPlan({ ...developmentPlan, training: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="الدورات والتدريبات المقترحة لتطوير أداء الموظف..."
                />
              </div>
            </div>
          </div>

          {/* Final Score Card */}
          <div className={`rounded-lg shadow-lg p-6 border-4 ${
            finalRating.color === 'green' ? 'bg-green-50 border-green-300' :
            finalRating.color === 'blue' ? 'bg-blue-50 border-blue-300' :
            finalRating.color === 'yellow' ? 'bg-yellow-50 border-yellow-300' :
            finalRating.color === 'orange' ? 'bg-orange-50 border-orange-300' :
            'bg-red-50 border-red-300'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-2">النتيجة النهائية</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-5xl font-black text-gray-800">{averageScore > 0 ? averageScore : '-'}</span>
                    <span className="text-2xl text-gray-600">/ 5</span>
                  </div>
                  {averageScore > 0 && (
                    <div className={`px-6 py-3 rounded-full font-black text-lg ${
                      finalRating.color === 'green' ? 'bg-green-200 text-green-800' :
                      finalRating.color === 'blue' ? 'bg-blue-200 text-blue-800' :
                      finalRating.color === 'yellow' ? 'bg-yellow-200 text-yellow-800' :
                      finalRating.color === 'orange' ? 'bg-orange-200 text-orange-800' :
                      'bg-red-200 text-red-800'
                    }`}>
                      {finalRating.label}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-10 h-10 ${
                      star <= Math.round(averageScore)
                        ? 'text-[#C8A870] fill-[#C8A870]'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/hr/performance/reviews')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
            >
              إلغاء
            </button>
            
            <button
              type="button"
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition-all disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              حفظ كمسودة
            </button>

            {hasPermission('hr.performance.create') && (
              <button
                type="button"
                onClick={() => handleSave('manager')}
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all disabled:opacity-50"
              >
                <CheckCircle className="w-5 h-5" />
                اعتماد من المدير المباشر
              </button>
            )}

            {hasPermission('hr.performance.approve') && (
              <button
                type="button"
                onClick={() => handleSave('hr')}
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all disabled:opacity-50"
              >
                <Award className="w-5 h-5" />
                اعتماد نهائي من HR
              </button>
            )}
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default PerformanceReviewFormPage;
